import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Plug,
  Settings,
  Shield,
  Menu,
  X,
  Calendar,
  Link as LinkIcon,
  LogOut,
  ShieldCheck,
  GraduationCap,
  AlertTriangle,
  Users,
  FileText,
} from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { ThemeToggle } from '../ThemeToggle';
import { OwtAppLauncher } from './OwtAppLauncher';
import { Button } from '@/components/ui/button';

interface MainLayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Security Report', href: '/weekly-report', icon: Calendar },
  { name: 'Employee Compliance', href: '/employee-compliance', icon: GraduationCap },
  { name: 'Group Mapping', href: '/group-mapping', icon: LinkIcon },
  { name: 'Project Analysis', href: '/project-analysis', icon: Shield },
  { name: 'Integrations', href: '/integrations', icon: Plug },
  { name: 'Software Validation', href: '/software-validation', icon: ShieldCheck },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Planned from the start of the project, never built — kept as a reminder.
// Record: docs/user-stories.md, ADO #11083 / #11082.
const plannedNavigation = [
  { name: 'Alerts', icon: AlertTriangle },
  { name: 'Users', icon: Users },
  { name: 'Reports', icon: FileText },
];

const isRouteActive = (pathname: string, href: string) =>
  href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);

interface NavLinksProps {
  items: typeof navigation;
  pathname: string;
  compact?: boolean;
  onNavigate?: () => void;
}

const NavLinks: React.FC<NavLinksProps> = ({ items, pathname, compact, onNavigate }) => (
  <>
    {items.map((item) => {
      const Icon = item.icon;
      const isActive = isRouteActive(pathname, item.href);
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={onNavigate}
          className={`flex items-center justify-between px-3 rounded-lg text-sm font-medium transition-colors ${
            compact ? 'py-2' : 'py-3'
          } ${
            isActive
              ? `bg-primary/10 text-primary${compact ? '' : ' border border-primary/20'}`
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}
        >
          <div className="flex items-center space-x-3">
            <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
            <span>{item.name}</span>
          </div>
        </Link>
      );
    })}
  </>
);

const PlannedLabels: React.FC<{ items: typeof plannedNavigation; compact?: boolean }> = ({
  items,
  compact,
}) => (
  <>
    {items.map(({ name, icon: Icon }) => (
      <div
        key={name}
        title="Planned — not built yet"
        className={`flex items-center space-x-3 px-3 rounded-lg text-sm font-medium text-muted-foreground/50 cursor-default ${
          compact ? 'py-2' : 'py-3'
        }`}
      >
        <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        <span>{name}</span>
      </div>
    ))}
  </>
);

const PlannedHeading: React.FC = () => (
  <div className="pt-4 mt-2 border-t border-border">
    <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
      Planned
    </p>
  </div>
);

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { instance } = useMsal();

  const handleLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-lg font-semibold text-foreground">Sentinel</span>
              </Link>
              <Button variant="ghost" size="sm" aria-label="Close menu" onClick={() => setSidebarOpen(false)}>
                <X />
              </Button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              <NavLinks
                items={navigation}
                pathname={location.pathname}
                compact
                onNavigate={() => setSidebarOpen(false)}
              />
              <PlannedHeading />
              <PlannedLabels items={plannedNavigation} compact />
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:w-64 lg:bg-card lg:border-r lg:border-border lg:flex lg:flex-col">
        <div className="flex items-center p-6 border-b border-border">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Sentinel</h1>
              <p className="text-xs text-muted-foreground">Security Platform</p>
            </div>
          </Link>
        </div>
        
        <nav className="flex-1 p-6 space-y-2">
          <NavLinks items={navigation} pathname={location.pathname} />
          <PlannedHeading />
          <PlannedLabels items={plannedNavigation} />
        </nav>

        <div className="p-6 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">OWT Security</div>
          <div className="text-xs text-muted-foreground">Management Platform</div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                aria-label="Open menu"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu />
              </Button>
              <Link to="/" className="flex items-center gap-2 lg:hidden">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-lg font-semibold text-foreground">Sentinel</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <OwtAppLauncher />
              <ThemeToggle />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut />
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;